import "./simple-pdf-tool.css";
import "./split-pdf.css";
import { createZipBlob, openBrowserPdf, pdfErrorMessage } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import { splitPdfRanges, splitZipName, validateSplitRanges } from "./split-pdf-core.js";

const root = document.querySelector("[data-split-pdf]");

if (root) {
  const ids = ["splitStatus", "splitUploadPanel", "splitDropZone", "chooseSplitPdf", "splitFileInput", "splitWorkspace", "splitFileName", "splitPageCount", "replaceSplitPdf", "addSplitRange", "splitRangeList", "splitPartCount", "createSplitZip", "splitProgress"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { bytes: null, fileName: "document.pdf", pageCount: 0, ranges: [], nextId: 1, busy: false };
  const operationResult = createOperationResult(root, elements.splitWorkspace);

  function showStatus(message = "") { elements.splitStatus.textContent = message; elements.splitStatus.hidden = !message; }
  function updateControls() {
    elements.splitPartCount.textContent = String(state.ranges.length);
    elements.createSplitZip.disabled = state.busy || !state.ranges.length;
    elements.addSplitRange.disabled = state.busy;
    elements.replaceSplitPdf.disabled = state.busy;
  }
  function renderRanges() {
    elements.splitRangeList.replaceChildren();
    state.ranges.forEach((range, index) => {
      const row = document.createElement("div"); row.className = "split-range-row";
      const start = numberInput(range.start, `Range ${index + 1} start page`, value => range.start = value);
      const end = numberInput(range.end, `Range ${index + 1} end page`, value => range.end = value);
      const name = document.createElement("span"); name.textContent = `Part ${index + 1}.pdf`;
      const remove = document.createElement("button"); remove.type = "button"; remove.textContent = "×"; remove.title = "Remove range"; remove.setAttribute("aria-label", `Remove range ${index + 1}`); remove.disabled = state.busy || state.ranges.length === 1;
      remove.addEventListener("click", () => { state.ranges = state.ranges.filter(item => item.id !== range.id); renderRanges(); });
      row.append(start, end, name, remove); elements.splitRangeList.append(row);
    });
    updateControls();
  }
  function numberInput(value, label, onChange) {
    const input = document.createElement("input"); input.type = "number"; input.min = "1"; input.max = String(state.pageCount); input.value = String(value); input.setAttribute("aria-label", label); input.disabled = state.busy;
    input.addEventListener("input", () => onChange(input.value)); return input;
  }
  function addRange(start = 1, end = state.pageCount) { state.ranges.push({ id: state.nextId++, start, end }); renderRanges(); }

  async function loadFile(file) {
    if (state.busy) return;
    showStatus(); state.busy = true; updateControls();
    let loaded = null;
    try {
      loaded = await openBrowserPdf(file);
      state.bytes = loaded.bytes; state.fileName = loaded.fileName; state.pageCount = loaded.pageCount;
      state.ranges = []; state.nextId = 1; addRange(1, loaded.pageCount);
      elements.splitFileName.textContent = loaded.fileName; elements.splitPageCount.textContent = String(loaded.pageCount);
      elements.splitUploadPanel.hidden = true; elements.splitWorkspace.hidden = false;
    } catch (error) { showStatus(pdfErrorMessage(error)); }
    finally { if (loaded?.document) await loaded.document.destroy(); state.busy = false; elements.splitFileInput.value = ""; renderRanges(); }
  }

  async function createSplit() {
    const validation = validateSplitRanges(state.ranges, state.pageCount);
    if (validation.error) { showStatus(validation.error); return; }
    showStatus(); state.busy = true; elements.splitProgress.hidden = false; renderRanges();
    try {
      const entries = await splitPdfRanges(state.bytes, validation.ranges, state.fileName, (current, total) => { elements.splitProgress.textContent = `Creating part ${current} of ${total}`; });
      operationResult.show({ blob: createZipBlob(entries), fileName: splitZipName(state.fileName), message: "Your PDF was split successfully.", details: [`${entries.length} ${entries.length === 1 ? "file" : "files"} in the ZIP`] });
    } catch (error) { showStatus(error?.message || "smallpdf.app could not split this PDF."); }
    finally { state.busy = false; elements.splitProgress.hidden = true; renderRanges(); }
  }

  elements.chooseSplitPdf.addEventListener("click", event => { event.stopPropagation(); elements.splitFileInput.click(); });
  elements.replaceSplitPdf.addEventListener("click", () => elements.splitFileInput.click());
  elements.splitFileInput.addEventListener("change", () => loadFile(elements.splitFileInput.files?.[0]));
  elements.addSplitRange.addEventListener("click", () => addRange());
  elements.createSplitZip.addEventListener("click", createSplit);
  elements.splitDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.splitFileInput.click(); });
  elements.splitDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.splitFileInput.click(); } });
  elements.splitDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.splitDropZone.classList.add("is-dragging"); });
  elements.splitDropZone.addEventListener("dragleave", () => elements.splitDropZone.classList.remove("is-dragging"));
  elements.splitDropZone.addEventListener("drop", event => { event.preventDefault(); elements.splitDropZone.classList.remove("is-dragging"); loadFile(event.dataTransfer.files?.[0]); });
}
